import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { notificationDatabaseService } from '../../../services/notificationDatabaseService';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import MashhadLogo from '../../../components/MashhadLogo';
import Sidebar from '../components/Sidebar';

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const unreadNotificationsCount = notifications.filter((n) => !n.isRead && !n.is_read).length;

  useEffect(() => {
    if (!userProfile?.id) return;
    let channel;
    const load = async () => {
      try {
        const data = await notificationDatabaseService.getMyNotifications();
        setNotifications(data || []);
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    };
    load();
    channel = notificationDatabaseService.subscribeToNotifications(userProfile.id, (newNotif) => {
      setNotifications((prev) => [newNotif, ...prev]);
    });
    return () => {
      if (channel) notificationDatabaseService.unsubscribeFromNotifications(channel);
    };
  }, [userProfile?.id]);

  // SAFE MODE: Auth System Fix - Logout redirect to customer login, never "/"
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/customer-login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Sidebar hidden globally — bottom navigation is used instead
  const showSidebar = false;

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* Sidebar - مخفي في لوحة التحكم وحجوزاتي وصفحات حسابي (بدون حذف، إخفاء فقط) */}
      {showSidebar && (
        <>
          <Sidebar 
            isOpen={sidebarOpen} 
            onClose={() => setSidebarOpen(false)} 
            unreadNotificationsCount={unreadNotificationsCount}
          />
          {sidebarOpen && (
            <div 
              className="fixed inset-0 bg-black/50 z-40 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </>
      )}

      {/* Main Content - هامش لليمين فقط عند إظهار القائمة الجانبية */}
      <div className={showSidebar ? 'md:mr-64 min-h-screen flex flex-col' : 'min-h-screen flex flex-col'}>
        {/* Header - ثابت عند التمرير مع مسافة آمنة من الأعلى (الـ notch وشريط الحالة) */}
        <header
          className="fixed left-0 right-0 z-30 bg-white border-b border-slate-200 shadow-sm"
          style={{ top: 'var(--app-download-banner-height, 0px)', paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
        >
          <div className="px-4 md:px-6 h-16 flex items-center justify-between gap-2">
            {/* يمين (RTL) - placeholder للتوازن */}
            <div className="flex-1 min-w-0" />

            {/* المنتصف - الشعار */}
            <div className="flex items-center justify-center flex-1 min-w-0 shrink-0">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="focus:outline-none hover:opacity-90 transition-opacity flex items-center"
                aria-label="الرئيسية"
              >
                <MashhadLogo variant="dark" size="lg" logoType="header" />
              </button>
            </div>

            {/* يسار (RTL) - إشعارات */}
            <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 justify-end">
              {/* Bell button - navigates directly to notifications page */}
              <button
                type="button"
                onClick={() => navigate('/customer-dashboard/notifications')}
                className="relative w-10 h-10 flex items-center justify-center rounded-2xl text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-all duration-200"
                aria-label="الإشعارات"
              >
                <Icon
                  name="Bell"
                  size={20}
                  className={unreadNotificationsCount > 0 ? 'text-primary' : ''}
                  strokeWidth={unreadNotificationsCount > 0 ? 2.2 : 1.8}
                />
                {unreadNotificationsCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none shadow-sm">
                    {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                  </span>
                )}
              </button>

              {/* User Menu - مخفي (التحكم من الهيدر)، الكود يبقى للاستخدام لاحقاً */}
              <div className="relative hidden" aria-hidden="true">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                    {userProfile?.avatar_url ? (
                      <Image
                        src={userProfile.avatar_url}
                        alt={userProfile.full_name}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                    ) : (
                      <Icon name="User" size={18} className="text-primary" />
                    )}
                  </div>
                  <div className="hidden md:block text-right">
                    <p className="text-sm font-semibold text-slate-900 leading-tight">
                      {userProfile?.full_name || 'مستخدم'}
                    </p>
                  </div>
                  <Icon name="ChevronDown" size={16} className="hidden md:block text-slate-400" />
                </button>

                {/* Dropdown Menu */}
                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-1 z-20">
                      <div className="px-4 py-3 border-b border-slate-100">
                        <p className="text-sm font-semibold text-slate-900">{userProfile?.full_name}</p>
                        <p className="text-xs text-slate-500 truncate">{userProfile?.email}</p>
                      </div>
                      <div className="py-1">
                        <button
                          onClick={() => { navigate('/customer-dashboard/profile'); setUserMenuOpen(false); }}
                          className="w-full px-4 py-2.5 text-right text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                        >
                          <Icon name="User" size={18} className="text-slate-400" />
                          <span>الملف الشخصي</span>
                        </button>
                        <button
                          onClick={() => { navigate('/customer-dashboard/bookings'); setUserMenuOpen(false); }}
                          className="w-full px-4 py-2.5 text-right text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-3"
                        >
                          <Icon name="Calendar" size={18} className="text-slate-400" />
                          <span>حجوزاتي</span>
                        </button>
                      </div>
                      <div className="border-t border-slate-100 py-1">
                        <button
                          onClick={handleLogout}
                          className="w-full px-4 py-2.5 text-right text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                        >
                          <Icon name="LogOut" size={18} className="text-red-500" />
                          <span>تسجيل الخروج</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content - مسافة علوية = ارتفاع الهيدر + المنطقة الآمنة + بانر التطبيق */}
        <main
          className="flex-1 p-4 md:p-6"
          style={{ paddingTop: 'calc(4rem + max(0.5rem, env(safe-area-inset-top)) + var(--app-download-banner-height, 0px))' }}
        >
          {/* زر الرجوع أعلى المحتوى */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 transition-colors mb-4 -mt-1"
            aria-label="رجوع"
          >
            <Icon name="ArrowRight" size={20} />
            <span className="text-sm font-medium">رجوع</span>
          </button>
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
